import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/solid';
import MobileLayout from '../../components/MobileLayout';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const ChangePassword: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error(t('settings.password.passwordsDoNotMatch', 'Passwords do not match'));
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      toast.error(t('settings.password.minLength', 'Password must be at least 8 characters long'));
      return;
    }

    setIsLoading(true);

    try {
      await axios.post('/users/change-password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });

      toast.success(t('settings.password.updateSuccess', 'Password updated successfully'));
      navigate('/profile');
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ||
          t('settings.password.updateError', 'Failed to update password'),
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MobileLayout>
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-dark-border dark:border-dark-border border-light-border">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/profile')}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-dark-surface dark:hover:bg-dark-surface hover:bg-light-surface"
            >
              <ArrowLeftIcon className="w-5 h-5 text-dark-text dark:text-dark-text text-light-text" />
            </button>
            <h1 className="ml-4 text-xl font-semibold text-dark-text dark:text-dark-text text-light-text">
              {t('settings.password.title', 'Change Password')}
            </h1>
          </div>
        </div>

        {/* Form */}
        <div className="flex-1 px-6 py-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label
                htmlFor="current-password"
                className="block text-sm font-medium text-dark-text dark:text-dark-text text-light-text mb-2"
              >
                {t('settings.password.currentPassword', 'Current Password')}
              </label>
              <input
                id="current-password"
                type="password"
                value={passwordForm.currentPassword}
                onChange={(e) =>
                  setPasswordForm({ ...passwordForm, currentPassword: e.target.value })
                }
                required
                className="w-full px-4 py-3 bg-light-surface dark:bg-dark-card border border-light-border dark:border-dark-border rounded-xl text-light-text dark:text-white placeholder-light-text-secondary dark:placeholder-dark-text-secondary focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder={t('settings.password.currentPlaceholder', 'Enter current password')}
              />
            </div>

            <div>
              <label
                htmlFor="new-password"
                className="block text-sm font-medium text-dark-text dark:text-dark-text text-light-text mb-2"
              >
                {t('settings.password.newPassword', 'New Password')}
              </label>
              <input
                id="new-password"
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                required
                minLength={8}
                className="w-full px-4 py-3 bg-light-surface dark:bg-dark-card border border-light-border dark:border-dark-border rounded-xl text-light-text dark:text-white placeholder-light-text-secondary dark:placeholder-dark-text-secondary focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder={t('settings.password.newPlaceholder', 'Enter new password')}
              />
              <p className="mt-2 text-xs text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary">
                {t('settings.password.requirements', 'At least 8 characters')}
              </p>
            </div>

            <div>
              <label
                htmlFor="confirm-password"
                className="block text-sm font-medium text-dark-text dark:text-dark-text text-light-text mb-2"
              >
                {t('settings.password.confirmPassword', 'Confirm New Password')}
              </label>
              <input
                id="confirm-password"
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) =>
                  setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })
                }
                required
                minLength={8}
                className="w-full px-4 py-3 bg-light-surface dark:bg-dark-card border border-light-border dark:border-dark-border rounded-xl text-light-text dark:text-white placeholder-light-text-secondary dark:placeholder-dark-text-secondary focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder={t('settings.password.confirmPlaceholder', 'Confirm new password')}
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-4 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-500/50 text-white font-semibold rounded-xl transition-colors disabled:cursor-not-allowed"
            >
              {isLoading
                ? t('settings.password.updating', 'Updating...')
                : t('settings.password.updateButton', 'Update Password')}
            </button>
          </form>
        </div>
      </div>
    </MobileLayout>
  );
};

export default ChangePassword;
